import React from "react";
import { Box, Dialog, IconButton, useTheme, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";

export interface PaymentProviderOption {
  id: string;
  label: string;
}

interface GeneratePaymentLinkPopupProps {
  open: boolean;
  onClose: () => void;
  providers: PaymentProviderOption[];
  selectedProviderId: string | null;
  onSelectProvider: (id: string) => void;
  percentage: number;
  onPercentageChange: (value: string) => void;
  hasMaxLinks: boolean;
  isGenerating: boolean;
  paymentLink: string | null;
  paymentLinkMessage: string | null;
  isError: boolean;
  isCopied: boolean;
  onCopy: () => void;
  onGenerate: () => void;
}

const GeneratePaymentLinkPopup: React.FC<GeneratePaymentLinkPopupProps> = ({
  open,
  onClose,
  providers,
  selectedProviderId,
  onSelectProvider,
  percentage,
  onPercentageChange,
  hasMaxLinks,
  isGenerating,
  paymentLink,
  paymentLinkMessage,
  isError,
  isCopied,
  onCopy,
  onGenerate,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          padding: 3,
          borderRadius: 2,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700}>
          Generate Payment Link
        </CustomTypography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <CustomTypography
        variant="body2"
        color="text.secondary"
        style={{ marginBottom: 30 }}
      >
        Select a payment provider to generate a payment link for this order.
      </CustomTypography>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        {providers.map((provider) => (
          <Box
            key={provider.id}
            onClick={() => onSelectProvider(provider.id)}
            sx={{
              borderRadius: 1,
              border: "2px solid",
              borderColor:
                selectedProviderId === provider.id
                  ? theme.palette.primary.main
                  : "divider",
              backgroundColor: "transparent",
              px: 2,
              py: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              {provider.label}
            </CustomTypography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2 }}>
        <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Percentage
        </CustomTypography>
        <TextField
          type="number"
          value={percentage === 0 ? "" : percentage}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value === 0) {
              onPercentageChange("");
            } else if (!isNaN(value) && value >= 1 && value <= 100) {
              onPercentageChange(e.target.value);
            }
          }}
          slotProps={{
            htmlInput: {
              step: 5,
              min: 10,
              max: 100,
            },
          }}
          fullWidth
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
              fontSize: "0.8rem",
              marginBottom: 2,
            },
          }}
        />
        <CustomTypography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontSize: "0.75rem" }}
        >
          Minimum percentage value is 10.
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontSize: "0.75rem" }}
        >
          You can generate a maximum of 5 payment links per order.
        </CustomTypography>
      </Box>

      {paymentLinkMessage && (
        <CustomTypography
          variant="body2"
          sx={{ mt: 2 }}
          color={
            isError ? theme.palette.error.main : theme.palette.success.main
          }
        >
          {paymentLinkMessage}
        </CustomTypography>
      )}

      {paymentLink && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.customColors.greyBodyBackground,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              fontSize: "0.8rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={paymentLink}
          >
            {paymentLink}
          </Box>
          <AppButton
            label={isCopied ? "Copied" : "Copy"}
            variant="grayscale"
            onClick={onCopy}
            customStyles={{
              minWidth: 80,
              minHeight: 32,
              fontSize: "0.75rem",
            }}
          />
        </Box>
      )}
      {!hasMaxLinks && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
          }}
        >
          <AppButton
            label="Cancel"
            variant="grayscale"
            onClick={onClose}
            customStyles={{ minWidth: 120 }}
          />
          <AppButton
            label={isGenerating ? "Generating..." : "Generate Link"}
            onClick={onGenerate}
            disabled={
              !selectedProviderId ||
              isGenerating ||
              percentage < 10 ||
              percentage > 100
            }
            customStyles={{ minWidth: 140 }}
          />
        </Box>
      )}
    </Dialog>
  );
};

export default GeneratePaymentLinkPopup;
